import React, { useEffect, useState, useCallback } from 'react';
import { EvaluationGroup } from '../../../../store/evaluation/types';
import Modal from '../../../../components/UI/Modal';
import { Heading, Text } from '../../../../components/Typography/Typography';
import {
  getEvaluationContext,
  EmbeddingContext,
  saveCritique,
} from '../../../../api/evaluationApi';
import Button from '../../../../components/UI/Button';
import CritiqueForm from './CritiqueForm';
import styles from './EvaluationModal.module.scss';
import ReactMarkdown from 'react-markdown';

interface Props {
  group: EvaluationGroup;
  isOpen: boolean;
  onClose: () => void;
  onNavigate?: (direction: 'prev' | 'next') => void;
  onGroupUpdate?: (updatedGroup: EvaluationGroup) => void;
}

const EvaluationModal: React.FC<Props> = ({
  group,
  isOpen,
  onClose,
  onNavigate,
  onGroupUpdate,
}) => {
  const [context, setContext] = useState<EmbeddingContext[]>([]);
  const [isLoadingContext, setIsLoadingContext] = useState(false);
  const [contextError, setContextError] = useState<string | null>(null);
  const [critiquingEvalId, setCritiquingEvalId] = useState<string | null>(null);

  // Get the first evaluation's trigger info for the title
  const firstEval = group.evaluations[0];
  const modalTitle = firstEval
    ? `${firstEval.trigger_type}${firstEval.trigger_id ? ` - ${firstEval.trigger_id}` : ''}`
    : group.trace_id;

  // Get unique input fields across all evaluations
  const uniqueInput = group.evaluations.reduce(
    (acc, evaluation) => {
      if (evaluation.input.question && !acc.question) acc.question = evaluation.input.question;
      if (evaluation.input.response && !acc.response) acc.response = evaluation.input.response;
      if (evaluation.input.ground_truth && !acc.ground_truth)
        acc.ground_truth = evaluation.input.ground_truth;
      if (evaluation.input.context?.length && !acc.context) acc.context = evaluation.input.context;
      return acc;
    },
    {} as { question?: string; response?: string; ground_truth?: string; context?: string[] },
  );

  useEffect(() => {
    const fetchContext = async () => {
      if (!uniqueInput.context?.length) return;

      setIsLoadingContext(true);
      setContextError(null);

      try {
        const contextData = await getEvaluationContext(group.evaluations[0].id);
        setContext(contextData);
      } catch (error) {
        setContextError('Failed to load context');
        console.error('Error fetching context:', error);
      } finally {
        setIsLoadingContext(false);
      }
    };

    if (isOpen && uniqueInput.context?.length) {
      fetchContext();
    }
  }, [group.evaluations, isOpen]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!onNavigate) return;

      if (e.key === 'ArrowLeft') {
        onNavigate('prev');
      } else if (e.key === 'ArrowRight') {
        onNavigate('next');
      }
    },
    [onNavigate],
  );

  useEffect(() => {
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  const handleCritiqueClick = (evaluationId: string) => {
    setCritiquingEvalId(evaluationId);
  };

  const handleCritiqueCancel = () => {
    setCritiquingEvalId(null);
  };

  const handleCritiqueSubmit = async (
    evaluationId: string,
    values: { human_scores: number; critique: string },
  ) => {
    try {
      await saveCritique(evaluationId, values);

      // Create a new group object with updated evaluations
      const updatedGroup = {
        ...group,
        evaluations: group.evaluations.map((evaluation) =>
          evaluation.id === evaluationId
            ? { ...evaluation, human_scores: values.human_scores, critique: values.critique }
            : evaluation,
        ),
      };

      // Call the update handler if provided
      if (onGroupUpdate) {
        onGroupUpdate(updatedGroup);
      }

      setCritiquingEvalId(null);
    } catch (error) {
      console.error('Error saving critique:', error);
      throw error;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={modalTitle} size="large">
      <div className={styles.modalContent}>
        <div className={styles.evaluations}>
          {group.evaluations.map((evaluation) => (
            <div key={evaluation.id} className={styles.evaluation}>
              <Heading level={3}>{evaluation.name}</Heading>
              <div className={styles.evaluationDetails}>
                <Text>Score: {evaluation.score}</Text>
                {evaluation.reason && <Text>Reason: {evaluation.reason}</Text>}
                {evaluation.human_scores !== null && (
                  <Text>Human Score: {evaluation.human_scores}</Text>
                )}
                {evaluation.critique && <Text>Critique: {evaluation.critique}</Text>}
              </div>
              {critiquingEvalId === evaluation.id ? (
                <CritiqueForm
                  onSubmit={(values) => handleCritiqueSubmit(evaluation.id, values)}
                  onCancel={handleCritiqueCancel}
                  initialValues={{
                    human_scores: evaluation.human_scores,
                    critique: evaluation.critique,
                  }}
                />
              ) : (
                <Button
                  variant="ghost"
                  size="small"
                  onClick={() => handleCritiqueClick(evaluation.id)}
                >
                  {evaluation.critique ? 'Edit Critique' : 'Add Critique'}
                </Button>
              )}
            </div>
          ))}
        </div>

        {Object.keys(uniqueInput).length > 0 && (
          <div className={styles.section}>
            <Heading level={3}>Input</Heading>
            {uniqueInput.question && (
              <div className={styles.inputField}>
                <Text weight="medium">Question</Text>
                <Text>{uniqueInput.question}</Text>
              </div>
            )}
            {uniqueInput.response && (
              <div className={styles.response}>
                <Text weight="medium">Response</Text>
                <ReactMarkdown>{uniqueInput.response}</ReactMarkdown>
              </div>
            )}
            {uniqueInput.ground_truth && (
              <div className={styles.inputField}>
                <Text weight="medium">Ground Truth</Text>
                <Text>{uniqueInput.ground_truth}</Text>
              </div>
            )}

            {uniqueInput.context && uniqueInput.context.length > 0 && (
              <div>
                <Text weight="medium">Context</Text>
                {isLoadingContext ? (
                  <Text color="neutral-600">Loading context...</Text>
                ) : contextError ? (
                  <Text color="red">{contextError}</Text>
                ) : (
                  <div className={styles.context}>
                    {context.map((item) => (
                      <div key={item.id} className={styles.contextItem}>
                        <div className={styles.contextHeader}>
                          <Text weight="medium" size="small" color="neutral-600">
                            {item.source}
                          </Text>
                        </div>
                        <Text>{item.context}</Text>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default EvaluationModal;
